import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PreviewService } from 'core/api/services/preview';
import { useExperiences } from 'core/contexts/experienceV2';
import { ExperienceStatus, formatCurrency, timeDurationFormatter } from '@utriper/core';
import { PreviewPriceGroup } from '@utriper/core-ui';
import { Box, Button, Grid2, Typography } from '@mui/material';
import { AccessTime, AttachMoney, Edit, Inventory2Outlined, Place, TaskAlt } from '@mui/icons-material';
import Placeholder from 'assets/images/placeholder.webp';
import ExperienceStatusChip from 'components/ExperienceStatusChip';

const ExperienceCard = ({ id, name, status, location, price, priceGroupId, currency, duration, pictureURL, handleStatus = () => {} }) => {
    const { t: translate, i18n } = useTranslation();
    const history = useHistory();
    const { handleExperience } = useExperiences();

    const imageSrc = pictureURL ? `${process.env.REACT_APP_CDN_URL}${pictureURL}` : Placeholder;

    return (
        <Grid2
            container
            size={{ xs: 12, sm: 6, md: 3 }}
            direction="column"
            sx={{
                padding: '1em',
                borderRadius: '.5em',
                border: 'solid #13131340 1px',
                boxShadow: 'var(--shadow-card)',
                backgroundColor: '#fff',
                gap: '1em',
                cursor: 'pointer',
                transition: 'all .2s ease-in-out',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0px 4px 20px 0px rgba(0,0,0,0.05)',
                },
                '&:active': {
                    transform: 'translateY(-1px) scale(0.99)',
                },
            }}
            onClick={() => handleExperience(id, history)}
        >
            {/* Image */}
            <Box sx={{ width: '100%', height: 'fit-content' }}>
                <Box
                    component="img"
                    src={imageSrc}
                    alt={`${name}-image`}
                    sx={{
                        aspectRatio: '16 / 9',
                        objectFit: 'cover',
                        objectPosition: 'center',
                        width: '100%',
                        height: 'auto',
                        borderRadius: '8px',
                    }}
                />
            </Box>
            {/* Data */}
            <Grid2 container size={12} spacing={1.5} direction="column">
                <Grid2 size={12}>
                    <ExperienceStatusChip status={status} />
                </Grid2>
                <Grid2
                    size={12}
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    <Typography fontSize={20} fontWeight={700} sx={{ color: '#131313', maxWidth: '100%' }} noWrap>
                        {name}
                    </Typography>
                </Grid2>
                {location && (
                    <Grid2 container size={12} spacing={1} alignItems="center" flexWrap="nowrap">
                        <Place />
                        <Typography fontSize={14} fontWeight={500} sx={{ color: '#131313', maxWidth: '100%' }} noWrap>
                            {location}
                        </Typography>
                    </Grid2>
                )}
                {((Number.isFinite(price) && currency) || priceGroupId) && (
                    <Grid2 container size={12} spacing={1} alignItems="center" flexWrap="nowrap">
                        <AttachMoney />
                        {priceGroupId ? (
                            <PreviewPriceGroup id={priceGroupId} label="name" previewService={PreviewService} styles={{ fontSize: '14px', color: '#131313', maxWidth: '100%', whiteSpace: 'break-spaces' }} route={`/price-group/${priceGroupId}`} />
                        ) : (
                            <Typography fontSize={14} fontWeight={500} sx={{ color: '#131313', maxWidth: '100%' }} noWrap>
                                {formatCurrency(price, currency, i18n.language)}
                            </Typography>
                        )}
                    </Grid2>
                )}
                {Number.isFinite(duration) && (
                    <Grid2 container size={12} spacing={1} alignItems="center" flexWrap="nowrap">
                        <AccessTime />
                        <Typography fontSize={14} fontWeight={500} sx={{ color: '#131313', maxWidth: '100%' }} noWrap>
                            {timeDurationFormatter(duration, translate)}
                        </Typography>
                    </Grid2>
                )}
            </Grid2>

            {/* Buttons */}
            <Grid2 container size={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1em', marginTop: 'auto', flexWrap: 'nowrap' }}>
                <Button
                    sx={{
                        fontWeight: '600',
                        width: '100%',
                        color: '#131313',
                        textTransform: 'none',
                        border: 'solid #13131340 1px',
                    }}
                    variant="outlined"
                    onClick={(e) => {
                        e.stopPropagation();
                        history.push(`/experience/${id}/edit`);
                    }}
                    startIcon={<Edit sx={{ color: '#131313' }} />}
                >
                    {translate('common.edit')}
                </Button>
                {status !== ExperienceStatus.IDS.live ? (
                    <Button
                        sx={{
                            fontWeight: '600',
                            width: '100%',
                            color: 'white',
                            textTransform: 'none',
                            backgroundColor: '#131313',
                        }}
                        variant="contained"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleStatus(id, ExperienceStatus.IDS.live);
                        }}
                        startIcon={<TaskAlt sx={{ color: 'white' }} />}
                    >
                        {translate('common.publish')}
                    </Button>
                ) : (
                    <Button
                        sx={{
                            fontWeight: '600',
                            width: '100%',
                            color: '#131313',
                            textTransform: 'none',
                            backgroundColor: '#13131326',
                        }}
                        variant="contained"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleStatus(id, ExperienceStatus.IDS.archived);
                        }}
                        startIcon={<Inventory2Outlined sx={{ color: '#131313' }} />}
                    >
                        {translate('common.to_archive')}
                    </Button>
                )}
            </Grid2>
        </Grid2>
    );
};

export default ExperienceCard;
